import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface RouteMeta {
  title: string;
  description: string;
}

const DEFAULT_META: RouteMeta = {
  title: 'Cardumy',
  description: 'O ecossistema definitivo de TCG para colecionadores e jogadores gerenciarem coleções, montarem decks, explorarem cartas e conectarem-se com lojas parceiras.'
};

const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: 'Cardumy',
    description: 'Gerencie sua coleção, monte e teste decks competitivos, explore cartas e encontre as melhores lojas parceiras em um só lugar.'
  },
  '/dashboard': {
    title: 'Início',
    description: 'Painel principal com novidades, eventos, cartas em alta e feeds da comunidade Cardumy.'
  },
  '/busca': {
    title: 'Busca de Cartas',
    description: 'Consulte preços, estoques, efeitos e detalhes de milhares de cartas de Pokémon, Magic, Yu-Gi-Oh!, One Piece e Lorcana.'
  },
  '/colecoes': {
    title: 'Expansões ',
    description: 'Acompanhe seu progresso de coleção nas expansões oficiais de seus card games favoritos.'
  },
  '/decks': {
    title: 'Decks da Comunidade',
    description: 'Descubra estratégias vencedoras, listas competitivas e decks criados pela comunidade Cardumy.'
  },
  '/deck-builder': {
    title: 'Criador de Decks',
    description: 'Monte, personalize e teste seus decks com estatísticas avançadas de custos e curva de mana/recursos.'
  },
  '/pastas': {
    title: 'Minhas Pastas',
    description: 'Organize suas cartas virtuais em pastas e fichários para colecionismo e trocas.'
  },
  '/lojas': {
    title: 'Lojas Parceiras',
    description: 'Conheça e compre de lojas físicas e online especializadas em TCG em todo o Brasil.'
  },
  '/cardlink': {
    title: 'CardLink',
    description: 'Conecte-se com outros colecionadores, marque as cartas de interesse e solicite contato através do CardLink.'
  },
  '/torneios': {
    title: 'Torneios',
    description: 'Inscreva-se em torneios locais, compre ingressos para eventos e acompanhe tabelas e pontuações.'
  },
  '/social': {
    title: 'Comunidade',
    description: 'Conecte-se com jogadores, compartilhe conquistas, fotos de aberturas e novidades do mundo dos TCGs.'
  },
  '/carrinho': {
    title: 'Carrinho',
    description: 'Finalize seus pedidos de cartas, acessórios e ingressos de eventos das lojas parceiras.'
  },
  '/glossario': {
    title: 'Glossário',
    description: 'Guia completo de termos, regras e palavras-chave dos principais Trading Card Games.'
  },
  '/como-jogar': {
    title: 'Como Jogar TCG',
    description: 'Guias oficiais passo a passo ensinando as regras, objetivos e fases de turno dos principais card games.'
  },
  '/suporte': {
    title: 'Central de Ajuda',
    description: 'Tire suas dúvidas, consulte tutoriais e entre em contato com a equipe do Cardumy.'
  },
  '/termos': {
    title: 'Termos e Condições',
    description: 'Termos e condições de uso, termos de compras e pagamentos, e política de privacidade do Cardumy TCG.'
  },
  '/termos-e-condicoes': {
    title: 'Termos e Condições',
    description: 'Termos e condições de uso, termos de compras e pagamentos, e política de privacidade do Cardumy TCG.'
  },
  '/termos-do-lojista': {
    title: 'Termos do Lojista',
    description: 'Termos e condições contratuais para lojas parceiras e organizadores no Cardumy TCG.'
  },
  '/login': {
    title: 'Entrar',
    description: 'Acesse sua conta Cardumy para gerenciar decks, coleções e compras.'
  },
  '/perfil': {
    title: 'Meu Perfil',
    description: 'Perfil de jogador no Cardumy com coleções, decks e estatísticas.'
  },
  '/pedidos': {
    title: 'Meus Pedidos',
    description: 'Acompanhe seus pedidos e compras nas lojas parceiras do Cardumy.'
  },
  '/ingressos': {
    title: 'Meus Ingressos',
    description: 'Visualize seus ingressos confirmados para torneios e eventos presenciais.'
  }
};

const setMetaTag = (selector: string, attribute: 'content' | 'href', value: string, createTag?: () => HTMLElement) => {
  let element = document.querySelector<HTMLElement>(selector);
  if (!element && createTag) {
    element = createTag();
    document.head.appendChild(element);
  }
  if (element) {
    element.setAttribute(attribute, value);
  }
};

export const SEO: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // 1. Resolve Title & Description for the Current Route
    let meta = ROUTE_META[location.pathname];

    if (!meta) {
      if (location.pathname.startsWith('/loja/')) {
        const storeName = location.pathname.split('/')[2] || 'Parceira';
        meta = {
          title: `Loja ${storeName} | Cardumy TCG`,
          description: `Confira o catálogo de cartas avulsas, produtos lacrados e eventos promovidos pela loja ${storeName} no Cardumy.`
        };
      } else if (location.pathname.startsWith('/deck/')) {
        meta = {
          title: 'Detalhes do Deck | Cardumy TCG',
          description: 'Visualize a lista de cartas, custos, raridades e estratégia completa deste deck.'
        };
      } else if (location.pathname.startsWith('/perfil/')) {
        meta = {
          title: 'Perfil de Usuário | Cardumy TCG',
          description: 'Veja coleções, decks públicos e conquistas do usuário no Cardumy.'
        };
      } else if (location.pathname.startsWith('/colecao/')) {
        meta = {
          title: 'Cartas da Coleção | Cardumy TCG',
          description: 'Lista completa de cartas e variantes desta coleção oficial.'
        };
      } else if (location.pathname.startsWith('/pasta/')) {
        meta = {
          title: 'Detalhes da Pasta | Cardumy TCG',
          description: 'Fichário de cartas virtuais da coleção no Cardumy.'
        };
      } else if (location.pathname.startsWith('/lojista/') || location.pathname.startsWith('/minha-loja')) {
        meta = {
          title: 'Painel do Lojista | Cardumy TCG',
          description: 'Gestão de catálogo, pedidos e vendas presenciais e online da sua loja no Cardumy.'
        };
      } else {
        meta = DEFAULT_META;
      }
    }

    // 2. Document Title
    document.title = meta.title;

    // 3. Meta Tags
    setMetaTag('meta[name="title"]', 'content', meta.title, () => {
      const el = document.createElement('meta');
      el.setAttribute('name', 'title');
      return el;
    });

    setMetaTag('meta[name="description"]', 'content', meta.description, () => {
      const el = document.createElement('meta');
      el.setAttribute('name', 'description');
      return el;
    });

    // 4. OpenGraph Tags
    setMetaTag('meta[property="og:title"]', 'content', meta.title, () => {
      const el = document.createElement('meta');
      el.setAttribute('property', 'og:title');
      return el;
    });

    setMetaTag('meta[property="og:description"]', 'content', meta.description, () => {
      const el = document.createElement('meta');
      el.setAttribute('property', 'og:description');
      return el;
    });

    // 5. Twitter Card Tags
    setMetaTag('meta[name="twitter:title"]', 'content', meta.title, () => {
      const el = document.createElement('meta');
      el.setAttribute('name', 'twitter:title');
      return el;
    });

    setMetaTag('meta[name="twitter:description"]', 'content', meta.description, () => {
      const el = document.createElement('meta');
      el.setAttribute('name', 'twitter:description');
      return el;
    });

    // 6. Canonical URL & Canonical OpenGraph/Twitter URLs
    const canonicalUrl = `${window.location.origin}${location.pathname}`;

    setMetaTag('link[rel="canonical"]', 'href', canonicalUrl, () => {
      const el = document.createElement('link');
      el.setAttribute('rel', 'canonical');
      return el;
    });

    setMetaTag('meta[property="og:url"]', 'content', canonicalUrl, () => {
      const el = document.createElement('meta');
      el.setAttribute('property', 'og:url');
      return el;
    });

    setMetaTag('meta[name="twitter:url"]', 'content', canonicalUrl, () => {
      const el = document.createElement('meta');
      el.setAttribute('name', 'twitter:url');
      return el;
    });
  }, [location.pathname]);

  return null;
};
